import React from "react";
import { View, StyleSheet } from "react-native";
import { CircleIcon } from "@phosphor-icons/react";

import { ProgressDetails } from "@axio/graphql/generated/graphql";
import { getSentimentColor } from "../../../../utils/analyticsUtils";
import { formatDurationFromSeconds } from "../../../../utils/timeUtils";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";

interface ProgressDetailsBlockProps {
  details?: ProgressDetails;
}

const ProgressDetailsBlock: React.FC<ProgressDetailsBlockProps> = ({ details }) => {
  // Use real data from GraphQL - no mock data
  const progressData = details;

  if (!progressData) return null;

  return (
    <View style={styles.progressDetailsContainer}>
      {/* Header with Level badge */}
      <View style={styles.progressDetailsHeader}>
        <CustomText text="Progress Details" size="l" weight="bold" />
        {progressData.level && (
          <View style={[styles.levelBadge, { backgroundColor: "#3B82F6" + "20", borderColor: "#3B82F6" }]}>
            <CustomText
              text={`Level: ${progressData.level}`}
              size="xs"
              style={{ color: "#3B82F6" }}
              weight="bold"
              useTranslationText={false}
            />
          </View>
        )}
      </View>

      <CustomSpacing type="vertical" size="m" />

      {/* Summary section */}
      {progressData.summary && (
        <View style={styles.summarySection}>
          <CustomText text="Summary" weight="bold" />
          <CustomSpacing type="vertical" size="xs" />
          <CustomText text={progressData.summary} size="s" useTranslationText={false} />
        </View>
      )}

      {/* Analytics metrics section - two column layout */}
      <View style={styles.analyticsMetricsSection}>
        <View style={styles.analyticsColumn}>
          {/* Sentiment and Originality badges */}
          <View style={styles.badgesRow}>
            {progressData.sentiment_analysis && (
              <View
                style={[
                  styles.analyticsBadge,
                  {
                    backgroundColor: getSentimentColor(progressData.sentiment_analysis) + "20",
                    borderColor: getSentimentColor(progressData.sentiment_analysis),
                  },
                ]}>
                <CustomText
                  text="sentiment_label"
                  size="xs"
                  weight="bold"
                  style={{ color: getSentimentColor(progressData.sentiment_analysis) }}
                />
                <CustomText
                  text={progressData.sentiment_analysis}
                  size="xs"
                  style={{
                    color: getSentimentColor(progressData.sentiment_analysis),
                    textTransform: "capitalize",
                  }}
                  useTranslationText={false}
                />
              </View>
            )}
            {progressData.originality_analysis && (
              <View style={[styles.originalityBadge, { backgroundColor: "#8B5CF6" + "20", borderColor: "#8B5CF6" }]}>
                <CustomText text="originality_label" size="xs" weight="bold" style={{ color: "#8B5CF6" }} />
                <CustomText
                  text={progressData.originality_analysis}
                  size="xs"
                  style={{ color: "#8B5CF6", flexShrink: 1 }}
                  useTranslationText={false}
                />
              </View>
            )}
          </View>

          {/* Message counts */}
          <View style={styles.badgesRow}>
            {progressData.questions_asked !== undefined && (
              <View style={[styles.analyticsBadge, { backgroundColor: "#3B82F6" + "20", borderColor: "#3B82F6" }]}>
                <CustomText text="questions_asked_label" size="xs" style={{ color: "#3B82F6" }} />
                <CustomText
                  text={progressData.questions_asked}
                  size="xs"
                  style={{ color: "#3B82F6" }}
                  useTranslationText={false}
                />
              </View>
            )}
            {progressData.student_messages_count !== undefined && (
              <View style={[styles.analyticsBadge, { backgroundColor: "#6366F1" + "20", borderColor: "#6366F1" }]}>
                <CustomText text="student_msgs_label" size="xs" style={{ color: "#6366F1" }} />
                <CustomText
                  text={progressData.student_messages_count}
                  size="xs"
                  style={{ color: "#6366F1" }}
                  useTranslationText={false}
                />
              </View>
            )}
            {progressData.ai_messages_count !== undefined && (
              <View style={[styles.analyticsBadge, { backgroundColor: "#8B5CF6" + "20", borderColor: "#8B5CF6" }]}>
                <CustomText text="ai_msgs_label" size="xs" style={{ color: "#8B5CF6" }} />
                <CustomText
                  text={progressData.ai_messages_count}
                  size="xs"
                  style={{ color: "#8B5CF6" }}
                  useTranslationText={false}
                />
              </View>
            )}
          </View>
        </View>

        <View style={styles.analyticsColumn}>
          {/* Response times */}
          {progressData.average_student_response_time !== undefined && (
            <View style={styles.metricRow}>
              <CustomText text="avg_student_response_label" size="s" style={{ color: "#6B7280" }} />
              <CustomText
                text={`${Math.round(progressData.average_student_response_time)}s`}
                size="s"
                weight="bold"
                useTranslationText={false}
              />
            </View>
          )}
          {progressData.average_ai_response_time !== undefined && (
            <View style={styles.metricRow}>
              <CustomText text="avg_ai_response_label" size="s" style={{ color: "#6B7280" }} />
              <CustomText
                text={`${Math.round(progressData.average_ai_response_time)}s`}
                size="s"
                weight="bold"
                useTranslationText={false}
              />
            </View>
          )}
          {progressData.total_time_taken !== undefined && (
            <View style={styles.metricRow}>
              <CustomText text="total_time_taken_label" size="s" style={{ color: "#6B7280" }} />
              <CustomText
                text={formatDurationFromSeconds(progressData.total_time_taken)}
                size="s"
                weight="bold"
                useTranslationText={false}
              />
            </View>
          )}
        </View>
      </View>

      <CustomSpacing type="vertical" size="s" />

      {/* Three-column points and recommendations section */}
      <View style={styles.pointsGrid}>
        <View style={[styles.pointsSection, { backgroundColor: "#10B981" + "10", borderColor: "#10B981" + "40" }]}>
          <View style={styles.pointsHeader}>
            <CircleIcon size={8} color="#10B981" weight="fill" style={styles.dotIcon} />
            <CustomText text="Success Points" size="s" weight="bold" style={{ color: "#10B981" }} />
          </View>
          <CustomSpacing type="vertical" size="xs" />
          {progressData.success_points && progressData.success_points.length > 0 ? (
            progressData.success_points.map((point, i) => (
              <View key={i} style={styles.pointItem}>
                <CircleIcon size={4} color="#10B981" weight="fill" style={styles.bulletIcon} />
                <CustomText text={point} size="xs" style={{ color: "#374151", flex: 1 }} useTranslationText={false} />
              </View>
            ))
          ) : (
            <CustomText text="None recorded" size="xs" style={{ color: "#6B7280", fontStyle: "italic" }} />
          )}
        </View>

        <View style={[styles.pointsSection, { backgroundColor: "#EF4444" + "10", borderColor: "#EF4444" + "40" }]}>
          <View style={styles.pointsHeader}>
            <CircleIcon size={8} color="#EF4444" weight="fill" style={styles.dotIcon} />
            <CustomText text="Confusion Points" size="s" weight="bold" style={{ color: "#EF4444" }} />
          </View>
          <CustomSpacing type="vertical" size="xs" />
          {progressData.confusion_points && progressData.confusion_points.length > 0 ? (
            progressData.confusion_points.map((point, i) => (
              <View key={i} style={styles.pointItem}>
                <CircleIcon size={4} color="#EF4444" weight="fill" style={styles.bulletIcon} />
                <CustomText text={point} size="xs" style={{ color: "#374151", flex: 1 }} useTranslationText={false} />
              </View>
            ))
          ) : (
            <CustomText text="None recorded" size="xs" style={{ color: "#6B7280", fontStyle: "italic" }} />
          )}
        </View>

        <View style={[styles.pointsSection, { backgroundColor: "#3B82F6" + "10", borderColor: "#3B82F6" + "40" }]}>
          <View style={styles.pointsHeader}>
            <CircleIcon size={8} color="#3B82F6" weight="fill" style={styles.dotIcon} />
            <CustomText text="Recommendations" size="s" weight="bold" style={{ color: "#3B82F6" }} />
          </View>
          <CustomSpacing type="vertical" size="xs" />
          {progressData.further_recommendations && progressData.further_recommendations.length > 0 ? (
            progressData.further_recommendations.map((rec, i) => (
              <View key={i} style={styles.pointItem}>
                <CircleIcon size={4} color="#3B82F6" weight="fill" style={styles.bulletIcon} />
                <CustomText text={rec} size="xs" style={{ color: "#374151", flex: 1 }} useTranslationText={false} />
              </View>
            ))
          ) : (
            <CustomText text="None provided" size="xs" style={{ color: "#6B7280", fontStyle: "italic" }} />
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  progressDetailsContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
  },
  progressDetailsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  levelBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  summarySection: {
    marginBottom: 12,
    padding: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  analyticsMetricsSection: {
    flexDirection: "row",
    gap: 16,
  },
  analyticsColumn: {
    flex: 1,
  },
  badgesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  analyticsBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  originalityBadge: {
    maxWidth: "100%",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 4,
  },
  metricRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  pointsGrid: {
    flexDirection: "row",
    gap: 12,
  },
  pointsSection: {
    flex: 1,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
  },
  pointsHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  pointItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 4,
  },
  dotIcon: {
    marginRight: 6,
  },
  bulletIcon: {
    marginRight: 6,
    marginTop: 7,
  },
});

export default ProgressDetailsBlock;
